import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Upload, Trash, BookOpen } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function Recipes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importMode, setImportMode] = useState("skip"); // skip | update | create_new
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/recipes");
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onImport = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(
        `/inventory/recipes/import?mode=${importMode}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const created = data.recipes_created || 0;
      const updated = data.recipes_updated || 0;
      const skipped = data.recipes_skipped || 0;
      const parts = [];
      if (created) parts.push(`${created} created`);
      if (updated) parts.push(`${updated} updated`);
      if (skipped) parts.push(`${skipped} skipped`);
      const summary = parts.length ? parts.join(" · ") : "no changes";
      if (created + updated === 0 && skipped > 0) {
        toast.info(`Import complete — ${summary}`, {
          description: "All recipes already existed. Switch mode to Update or Create-new to change behaviour.",
        });
      } else {
        toast.success(`Import complete — ${summary}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      // Allow re-selecting the same file (important for iterative testing)
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this recipe?")) return;
    try {
      await api.delete(`/inventory/recipes/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="inv-recipes-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
            Inventory · Recipes
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Recipe Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload Ziply5 recipe workbooks (Biryani Seasoning / Cooking / Masala format) — each sheet becomes a versioned recipe.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 h-10 pl-3 pr-1 rounded-md border border-slate-200 bg-white">
            <label
              htmlFor="recipes-import-mode"
              className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold"
            >
              On duplicate
            </label>
            <select
              id="recipes-import-mode"
              value={importMode}
              onChange={(e) => setImportMode(e.target.value)}
              className="h-8 text-xs font-medium text-slate-700 bg-transparent focus:outline-none pr-2"
              data-testid="recipes-import-mode"
            >
              <option value="skip">Skip existing</option>
              <option value="update">Update existing (new version)</option>
              <option value="create_new">Create new copy (v2, v3…)</option>
            </select>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => onImport(e.target.files?.[0])}
            data-testid="recipes-import-file"
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            data-testid="recipes-import-btn"
          >
            <Upload size={14} weight="bold" /> Import Workbook
          </button>
        </div>
      </div>

      {!loading && rows.length === 0 && (
        <div className="bg-white border border-dashed border-slate-200 rounded-lg p-12 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
            <BookOpen size={28} weight="duotone" />
          </div>
          <div className="font-heading text-lg font-semibold text-slate-900">No recipes yet</div>
          <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Upload your Ziply5 production recipes workbook to bootstrap the system. Each sheet (Seasoning, Cooking, Masala…) creates one recipe with auto-linked ingredients.
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            data-testid="recipes-import-empty-btn"
          >
            <Upload size={14} weight="bold" /> Import your first workbook
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-sm transition" data-testid={`recipe-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{r.code}</div>
                  <div className="font-heading text-lg font-bold text-slate-900 mt-1">{r.name}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${statusTone(r.status)}`}>{r.status}</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <Stat label="Items" value={(r.items || []).length} />
                <Stat label="Version" value={`v${r.version || 1}`} />
                <Stat label="Basis" value={`${r.batch_size_basis} kg`} />
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Link
                  to={`/inventory/batch-calculator?recipe=${r.id}`}
                  className="flex-1 text-center h-9 inline-flex items-center justify-center text-xs font-medium rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  data-testid={`recipe-calc-${r.id}`}
                >
                  Run Batch Calculator
                </Link>
                <button
                  onClick={() => onDelete(r.id)}
                  className="h-9 px-3 rounded-md border border-slate-200 text-rose-500 hover:bg-rose-50"
                  data-testid={`recipe-delete-${r.id}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-md p-2 text-center">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function statusTone(s) {
  const m = {
    draft: "bg-slate-100 text-slate-600",
    review: "bg-amber-50 text-amber-700",
    approved: "bg-emerald-50 text-emerald-700",
    superseded: "bg-slate-100 text-slate-500",
    archived: "bg-slate-100 text-slate-400",
  };
  return m[s] || "bg-slate-100 text-slate-600";
}
